'use no memo';
/* eslint-disable react-hooks/exhaustive-deps, react-hooks/rules-of-hooks, react-hooks/react-compiler */
/**
 * We intentionally break the rules of React here in order to facilitate a migration to
 * Syncronous flux. This will not work if a subtree is switched from Sync to Async at
 * runtime which we should not do.
 *
 * In the sync case we rely on React's official recommendation, useSyncExternalStoreWithSelector,
 * this is the same hook Redux uses. However, if we run it even in the async case to honor
 * the rule of react we'll pay double the render cost.
 */
import {useLayoutEffect, useState, useRef} from 'react';
import {
  useUnsafeInternalFluxContext,
  useSyncFluxContext,
} from './useFluxContext';
import {TFlux} from '../types';
import {
  type FluxMetricsContext,
  incrementFluxMetric,
} from '@packages/systems/designer-telemetry';
import {useSyncExternalStoreWithSelector} from 'use-sync-external-store/shim/with-selector';
import {weakMemo} from '@packages/utilities/memo';

function isReferentiallyEqual(a: unknown, b: unknown) {
  return a === b;
}

/** Stable fallback for mock/test Flux objects that lack getMetricsContext. */
const noopGetMetricsContext = (): FluxMetricsContext | undefined => undefined;

/**
 * wrap[FunctionName]WithMetrics
 *
 * weakMemo wrappers that produce a stable function reference for a given
 * (getMetricsContext, selector/equalityFn) pair, ensuring memoisation is
 * preserved by React hooks or libraries (such as useSyncExternalStoreWithSelector)
 * that depend on identity of the function.
 *
 * Same input function → same wrapper (memoisation preserved).
 * Different input function → different wrapper (re-evaluation triggered).
 *
 * The outer weakMemo is keyed on the getter (stable per Flux instance).
 * The inner weakMemo is keyed on the selector/equalityFn.
 */
export const wrapSelectorWithMetrics = weakMemo(
  (getMetricsContext: () => FluxMetricsContext | undefined) =>
    weakMemo(
      <State, Selected>(selector: (state: State) => Selected) =>
        (state: State): Selected => {
          incrementFluxMetric(getMetricsContext(), 'selectorsInvokedCount');
          return selector(state);
        }
    )
);

export const wrapEqualityFnWithMetrics = weakMemo(
  (getMetricsContext: () => FluxMetricsContext | undefined) =>
    weakMemo(
      <Selected>(equalityFn: (a: Selected, b: Selected) => boolean) =>
        (a: Selected, b: Selected): boolean => {
          const isEqual = equalityFn(a, b);
          if (!isEqual) {
            incrementFluxMetric(getMetricsContext(), 'selectorsChangedCount');
          }
          return isEqual;
        }
    )
);

interface UseSelector<State extends Record<any, any> = Record<any, any>> {
  <Selected = unknown>(
    selector: (state: State) => Selected,
    equalityFn?: (a: Selected, b: Selected) => boolean
  ): Selected;
}

/**
 * Runs a selector against our Flux state.
 */
export function runSelector<State, Selected>(
  selector: (state: State) => Selected,
  state: State,
  metricsContext: FluxMetricsContext | undefined
) {
  // Track selector invocation (incrementFluxMetric is a no-op if ctx is undefined)
  incrementFluxMetric(metricsContext, 'selectorsInvokedCount');

  return selector(state);
}

const getInitialSelectorState = weakMemo(
  <State extends Record<string, any>, Selected>(flux: TFlux<State>) =>
    weakMemo((selector: (state: State) => Selected) => {
      return () =>
        runSelector(selector, flux.state, flux.getMetricsContext?.());
    })
);

const createSubscription = weakMemo(
  <State extends Record<string, any>, Selected>(flux: TFlux<State>) =>
    weakMemo((equalityFn: (a: Selected, b: Selected) => boolean) =>
      weakMemo((stateRef: React.MutableRefObject<Selected>) =>
        weakMemo((setState: React.Dispatch<React.SetStateAction<Selected>>) =>
          weakMemo((selector: (State: State) => Selected) => {
            return () => {
              function handleUseSelectorChange() {
                const metricsCtx = flux.getMetricsContext?.();
                const prevSelectedState = stateRef.current;
                const nextSelectedState = runSelector(
                  selector,
                  flux.state,
                  metricsCtx
                );

                if (equalityFn(prevSelectedState, nextSelectedState)) return;

                // Eagerly update the ref so that subsequent subscription
                // callbacks (fired before React re-renders) compare against
                // the latest pending state, not the stale rendered state.
                // This mirrors React-Redux v7's checkForUpdates pattern.
                stateRef.current = nextSelectedState;

                setState((_prevSelectedState) => {
                  // Track selector changes (incrementFluxMetric is a no-op if ctx is undefined)
                  incrementFluxMetric(metricsCtx, 'selectorsChangedCount');
                  return nextSelectedState;
                });
              }
              return flux.onChange(handleUseSelectorChange);
            };
          })
        )
      )
    )
);

/**
 * Creates a typed selector hook that provides type safety for selecting state from the flux store,
 * use this hook throughout your components and subsystem.
 *
 * @type State - The state shape of your stores
 * @returns A React hook that accepts a selector function and optional equality function
 *
 * @example
 * ```tsx
 * // Define your state type
 * type AppState = {
 *   users: {
 *     byId: Record<string, User>;
 *     currentUserId: string | null;
 *   };
 *   ui: {
 *     isLoading: boolean;
 *     theme: 'light' | 'dark';
 *   };
 * };
 *
 * // Create a typed selector hook to use throughout your sub system
 * const useAppSelector = createUseSelector<AppState>();
 *
 * // Create reusable selectors, selectors must be memoized!
 * const getCurrentUser = singleMemo(usersById => singleMemo(currentUserId =>
 *   currentUserId ? usersById[currentUserId] : undefined
 * ));
 * const selectCurrentUser = ({users}: AppState) =>
 *   getCurrentUser(users.byId)(users.currentUserId)
 *
 * // Use in a component
 * function UserProfile() {
 *   // Use with a pre-defined selector
 *   const currentUser = useAppSelector(selectCurrentUser);
 *
 *   // Use with a custom equality function
 *   const userDetails = useAppSelector(
 *     selectCurrentUser,
 *     shallowEqual // prevents re-renders when objects are equivalent
 *   );
 *
 *   if (!currentUser) return <div>No user selected</div>;
 *
 *   return (
 *     <div className={`profile-${theme}`}>
 *       <h1>{currentUser.name}</h1>
 *       <p>{currentUser.email}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function createUseSelector<
  State extends Record<any, any> = Record<any, any>,
>(): UseSelector<State> {
  const useSyncSelector = createUseSelectorSync<State>();

  function useSelector<Selected = unknown>(
    selector: (state: State) => Selected,
    equalityFn: (a: Selected, b: Selected) => boolean = isReferentiallyEqual
  ): Selected {
    const flux = useUnsafeInternalFluxContext() as TFlux<State>;
    const isSync = useSyncFluxContext();

    if (isSync) {
      return useSyncSelector(selector, equalityFn);
    } else {
      //  Taken from react-redux implementation of `useSelector`:
      //
      //  We need useLayoutEffect to ensure the store subscription callback always
      //  has the selector from the latest render commit available, otherwise a
      //  store update may happen between render and the effect, which may cause
      //  missed updates; we also must ensure the store subscription is created
      //  synchronously, otherwise a store update may occur before the subscription
      //  is created and an inconsistent state may be observed
      const [selectedState, setState] = useState(
        getInitialSelectorState<State, Selected>(flux)(selector)
      );

      const stateRef = useRef(selectedState);
      stateRef.current = selectedState;

      useLayoutEffect(
        createSubscription<State, Selected>(flux)(equalityFn)(stateRef)(
          setState
        )(selector),
        [selector]
      );

      return selectedState;
    }
  }

  return useSelector;
}

export function createUseSelectorSync<
  State extends Record<any, any> = Record<any, any>,
>(): UseSelector<State> {
  function useSelector<Selected = unknown>(
    selector: (state: State) => Selected,
    equalityFn: (a: Selected, b: Selected) => boolean = isReferentiallyEqual
  ): Selected {
    const flux_ = useUnsafeInternalFluxContext() as TFlux<State>;
    const getCtx = flux_.getMetricsContext ?? noopGetMetricsContext;

    return useSyncExternalStoreWithSelector(
      flux_.onChangeSync,
      flux_.getState,
      flux_.getState,
      wrapSelectorWithMetrics(getCtx)(selector),
      wrapEqualityFnWithMetrics(getCtx)(equalityFn)
    );
  }

  return useSelector;
}
